import { useEffect, useState, useCallback } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Search, MapPin, Building, ClipboardList, Star, Briefcase } from 'lucide-react'
import { useAuth } from '../context/AuthContext'
import JobCard from '../components/JobCard'
import AdCard from '../components/AdCard'
import HomeNotifyPrompt from '../components/HomeNotifyPrompt'
import api from '../api/axios'
import { CITIES, BAKU_DISTRICTS } from '../utils/locations'
import { CategoryIcon } from '../utils/categoryIcons'

function getInitialView() {
  try { return localStorage.getItem('jobsView') || 'grid' } catch { return 'grid' }
}
function saveView(v) { try { localStorage.setItem('jobsView', v) } catch {} }

const SORT_OPTIONS = [
  { value: 'newest',      label: 'Ən yeni' },
  { value: 'oldest',      label: 'Ən köhnə' },
  { value: 'budget_desc', label: 'Məbləğ: Çoxdan aza' },
  { value: 'budget_asc',  label: 'Məbləğ: Azdan çoxa' },
]

const PAGE_SIZE = 10

function pageRange(current, total) {
  const delta = 2
  const start = Math.max(0, current - delta)
  const end   = Math.min(total - 1, current + delta)
  const pages = []
  for (let i = start; i <= end; i++) pages.push(i)
  return pages
}

export default function Jobs() {
  const { user }    = useAuth()
  const navigate    = useNavigate()

  const [jobs,          setJobs]          = useState([])
  const [totalElements, setTotalElements] = useState(0)
  const [totalPages,    setTotalPages]    = useState(0)
  const [page,          setPage]          = useState(0)
  const [loading,       setLoading]       = useState(true)

  const [workerProfiles,     setWorkerProfiles]     = useState([])
  const [workersLoading,     setWorkersLoading]     = useState(false)
  const [showTgPopup,        setShowTgPopup]        = useState(false)

  const [searchInput,  setSearchInput]  = useState('')
  const [search,       setSearch]       = useState('')
  const [category,     setCategory]     = useState('Hamısı')
  const [categoryQuery, setCategoryQuery] = useState('')
  const [city,         setCity]         = useState('')
  const [district,     setDistrict]     = useState('')
  const [sort,         setSort]         = useState('newest')
  const [view,         setView]         = useState(getInitialView) // 'grid' | 'list'

  // Feed reklamları — max 2 ədəd
  const [feedAds, setFeedAds] = useState([])
  const [categories, setCategories] = useState([])
  const [cityCounts, setCityCounts] = useState({})

  useEffect(() => {
    api.get('/ads/feed').then(r => setFeedAds(r.data || [])).catch(() => {})
    api.get('/jobs/city-counts').then(r => setCityCounts(r.data || {})).catch(() => {})
    api.get('/categories').then(r => setCategories(r.data || [])).catch(() => {})
  }, [])

  useEffect(() => {
    if (!user) return
    const key = `onboard_dismissed_${user.role}`
    try {
      if (!sessionStorage.getItem(key)) setShowTgPopup(true)
    } catch {}
  }, [user])

  const dismissTgPopup = () => {
    try { sessionStorage.setItem(`onboard_dismissed_${user?.role}`, '1') } catch {}
    setShowTgPopup(false)
  }

  useEffect(() => {
    // Employers AND guests see worker profiles; logged-in workers see the jobs feed.
    if (user && user.role !== 'EMPLOYER') return
    setWorkersLoading(true)
    api.get('/worker-profiles/browse', { params: { page: 0, size: 8 } })
      .then(r => setWorkerProfiles(r.data.content || []))
      .catch(() => {})
      .finally(() => setWorkersLoading(false))
  }, [user])

  const fetchJobs = useCallback((pg, cat, cty, dist, srch, srt) => {
    setLoading(true)
    const params = { page: pg, size: PAGE_SIZE, sort: srt }
    if (cat && cat !== 'Hamısı') params.category = cat
    if (cty)                     params.city     = cty
    if (dist)                    params.district  = dist
    if (srch && srch.trim())     params.search   = srch.trim()

    api.get('/jobs', { params })
      .then(r => {
        setJobs(r.data.content)
        setTotalElements(r.data.totalElements)
        setTotalPages(r.data.totalPages)
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => {
    fetchJobs(page, category, city, district, search, sort)
  }, [page, category, city, district, search, sort, fetchJobs])

  // Axtarış debounce
  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput); setPage(0) }, 500)
    return () => clearTimeout(t)
  }, [searchInput])

  function handleCategory(cat) { setCategory(cat); setPage(0) }

  function handleCity(val) {
    setCity(val)
    setDistrict('')   // şəhər dəyişəndə rayonu sıfırla
    setPage(0)
  }

  function handleDistrict(val) { setDistrict(val); setPage(0) }
  function handleSort(val)     { setSort(val);     setPage(0) }

  // Grid items — reklamları 2-ci (index 1) və sondan 2-ci yerə (index n-2) yerləşdir
  function buildGridItems() {
    if (jobs.length === 0) return []
    const items = jobs.map(j => ({ type: 'job', data: j }))
    const isLastPage = page >= totalPages - 1

    const ad0 = feedAds[0]
    if (ad0 && items.length >= 2) {
      items.splice(1, 0, { type: 'ad', data: ad0 })
    }

    const ad1 = feedAds[1]
    if (ad1 && !isLastPage && items.length >= 4) {
      items.splice(items.length - 1, 0, { type: 'ad', data: ad1 })
    }

    return items
  }

  const gridItems = buildGridItems()
  const pages     = pageRange(page, totalPages)
  const isBaku    = city === 'Bakı'
  const sortedCategories = [...categories].sort((a, b) => {
    const diff = (b.jobCount ?? 0) - (a.jobCount ?? 0)
    return diff !== 0 ? diff : (a.priority ?? 999) - (b.priority ?? 999)
  })
  const categoryOptions = [{ id: 'all', name: 'Hamısı', iconKey: 'briefcase' }, ...sortedCategories]

  // Kateqoriya axtarışı — case-insensitive, kiçik hərflə müqayisə.
  // Seçilmiş kateqoriya və boş sorğuda "Hamısı" həmişə görünür.
  const categoryQ = categoryQuery.trim().toLocaleLowerCase('az')
  const visibleCategoryOptions = categoryOptions.filter(opt => {
    if (opt.name === category) return true
    if (!categoryQ) return true
    if (opt.name === 'Hamısı') return false
    return opt.name.toLocaleLowerCase('az').includes(categoryQ)
  })

  return (
    <div className="container page-body">

      {/* ── Onboarding popup — role-specific, once per session ── */}
      {showTgPopup && user && (
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 1000,
            background: 'rgba(0,0,0,.82)', backdropFilter: 'blur(6px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '1rem',
          }}
          onClick={(e) => e.target === e.currentTarget && dismissTgPopup()}
        >
          <div style={{
            background: 'var(--card)', border: '1px solid rgba(255,255,255,.1)',
            borderRadius: 'var(--radius)', padding: '2rem', maxWidth: 440, width: '100%',
            position: 'relative',
          }}>
            <button onClick={dismissTgPopup} aria-label="Bağla" style={{
              position: 'absolute', top: '.75rem', right: '.75rem',
              background: 'none', border: 'none', cursor: 'pointer',
              color: 'var(--muted)', fontSize: '1.2rem', lineHeight: 1, padding: '.2rem',
            }}>×</button>

            {user.role === 'WORKER' ? (
              /* ── İşçi üçün ── */
              <>
                <div style={{ fontSize: '2.2rem', marginBottom: '.85rem' }}>👷</div>
                <h2 style={{ fontWeight: 800, fontSize: '1.15rem', marginBottom: '.5rem' }}>
                  Profilinizi yaradın — işəgötürənlər sizi tapsın
                </h2>
                <p style={{ fontSize: '.87rem', color: 'var(--muted)', lineHeight: 1.7, marginBottom: '.85rem' }}>
                  Profilinizi dərc edin — işəgötürənlər aktiv olaraq işçi axtarır.
                  Kateqoriya üzrə uyğun vakansiyalar birbaşa sizə çatır.
                </p>
                <div style={{
                  background: 'rgba(var(--accent-rgb),.07)', border: '1px solid rgba(var(--accent-rgb),.18)',
                  borderRadius: 8, padding: '.65rem .9rem', marginBottom: '1.25rem',
                  fontSize: '.8rem', color: 'var(--muted)', lineHeight: 1.6,
                }}>
                  ✓ Kateqoriyanızı, təcrübənizi və qiymətinizi yazın<br />
                  ✓ İşəgötürənlər sizi birbaşa tapsın və dəvət göndərsin<br />
                  ✓ Müraciətsiz işə düzəlmək şansı
                </div>
                <div style={{ display: 'flex', gap: '.75rem' }}>
                  <button
                    className="btn btn-primary btn-full"
                    style={{ flex: 2, fontSize: '.92rem' }}
                    onClick={() => { dismissTgPopup(); navigate('/worker-profiles') }}
                  >
                    Profil yarat
                  </button>
                  <button
                    className="btn btn-ghost"
                    style={{ flex: 1, fontSize: '.88rem' }}
                    onClick={dismissTgPopup}
                  >
                    Elanlara bax
                  </button>
                </div>
              </>
            ) : user.role === 'EMPLOYER' ? (
              /* ── İşəgötürən üçün ── */
              <>
                <div style={{ fontSize: '2.2rem', marginBottom: '.85rem' }}>💼</div>
                <h2 style={{ fontWeight: 800, fontSize: '1.15rem', marginBottom: '.5rem' }}>
                  Elanınızı paylaşın — uyğun işçi tapın
                </h2>
                <p style={{ fontSize: '.87rem', color: 'var(--muted)', lineHeight: 1.7, marginBottom: '.85rem' }}>
                  Elanınızı dərc edin — uyğun işçilər müraciət etsin.
                  Hər elan kateqoriyaya uyğun işçilərə göstərilir.
                </p>
                <div style={{
                  background: 'rgba(96,165,250,.07)', border: '1px solid rgba(96,165,250,.18)',
                  borderRadius: 8, padding: '.65rem .9rem', marginBottom: '1.25rem',
                  fontSize: '.8rem', color: 'var(--muted)', lineHeight: 1.6,
                }}>
                  ✓ Elanınız dəqiqələr içində yayımlanır<br />
                  ✓ Kateqoriya üzrə işçilər bildiriş alır<br />
                  ✓ Müraciəti chat vasitəsilə idarə edin
                </div>
                <div style={{ display: 'flex', gap: '.75rem' }}>
                  <button
                    className="btn btn-primary btn-full"
                    style={{ flex: 2, fontSize: '.92rem' }}
                    onClick={() => { dismissTgPopup(); navigate('/post-job') }}
                  >
                    Elan yerləşdir
                  </button>
                  <a
                    href="https://1ishvar.az/workers"
                    className="btn btn-ghost"
                    style={{ flex: 1, fontSize: '.88rem', textDecoration: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    onClick={dismissTgPopup}
                  >
                    İşçiləri kəşf et
                  </a>
                </div>
              </>
            ) : null}
          </div>
        </div>
      )}

      {/* Hero */}
      <div className="fade-up" style={{ marginBottom: '2rem' }}>
        <h1 className="page-title">Aktiv Elanlar</h1>
        <p className="page-subtitle">
          {totalElements} elan mövcuddur
          {user?.role === 'EMPLOYER' && (
            <> · <Link to="/post-job" style={{ color: 'var(--accent)', textDecoration: 'none', fontWeight: 600 }}>
              + Elan yerləşdir
            </Link></>
          )}
          {!user && (
            <> · Müraciət etmək üçün <Link to="/register" style={{ color: 'var(--accent)', textDecoration: 'none', fontWeight: 600 }}>
              qeydiyyatdan keçin
            </Link></>
          )}
        </p>
      </div>

      {/* ── Bildiriş opt-in (qonaqlar üçün də işləyir) ── */}
      <HomeNotifyPrompt />

      {/* ─── İşçi Profilləri (işəgötürənlər + qonaqlar üçün; işçilər elan feed-ini görür) ─── */}
      {(!user || user.role === 'EMPLOYER') && (
        <div className="fade-up" style={{ marginBottom: '2rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h2 style={{ fontWeight: 700, fontSize: '1rem', margin: 0 }}>İşçi Profilləri</h2>
            <Link
              to="/workers"
              style={{ fontSize: '.82rem', color: 'var(--accent)', textDecoration: 'none', fontWeight: 600 }}
            >
              Hamısına bax →
            </Link>
          </div>

          {workersLoading ? (
            <div style={{ display: 'flex', gap: '1rem', overflowX: 'auto', paddingBottom: '.5rem' }}>
              {[...Array(4)].map((_, i) => (
                <div key={i} style={{
                  minWidth: 200, height: 160, borderRadius: 'var(--radius)',
                  background: 'rgba(255,255,255,.04)', border: '1px solid var(--border)', flexShrink: 0,
                }} />
              ))}
            </div>
          ) : workerProfiles.length === 0 ? (
            <div style={{
              padding: '1.5rem', borderRadius: 'var(--radius)',
              background: 'rgba(255,255,255,.03)', border: '1px dashed rgba(255,255,255,.1)',
              textAlign: 'center', color: 'var(--muted)', fontSize: '.82rem',
            }}>
              Hələ aktiv işçi profili yoxdur. <Link to="/workers" style={{ color: 'var(--accent)' }}>Hamısına bax →</Link>
            </div>
          ) : (
            <div className="scroll-row-wrap">
              <div className="scroll-row" style={{ gap: '1rem' }}>
                {workerProfiles.map(profile => {
                  const handle = profile.userUsername || profile.userId
                  const initials = (profile.userName || '?')
                    .split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase()
                  return (
                    <div
                      key={profile.id}
                      className="card card-interactive"
                      onClick={() => navigate(`/users/${handle}`)}
                      style={{
                        minWidth: 200, maxWidth: 220, flexShrink: 0,
                        padding: '1.25rem', cursor: 'pointer',
                        display: 'flex', flexDirection: 'column', gap: '.75rem',
                      }}
                    >
                      {/* Avatar + name */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '.75rem' }}>
                        <div style={{
                          width: 40, height: 40, borderRadius: '50%', flexShrink: 0, overflow: 'hidden',
                          background: 'rgba(var(--accent-rgb),.1)', border: '2px solid rgba(var(--accent-rgb),.2)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: '.9rem', fontWeight: 700, color: 'var(--accent)',
                        }}>
                          {profile.userPhotoUrl
                            ? <img loading="lazy" src={profile.userPhotoUrl} alt={profile.userName}
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            : initials}
                        </div>
                        <div style={{ minWidth: 0 }}>
                          <div style={{
                            fontWeight: 700, fontSize: '.85rem',
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                          }}>
                            {profile.userName}
                          </div>
                          <div style={{
                            fontSize: '.72rem', color: 'var(--muted)',
                            display: 'flex', alignItems: 'center', gap: '.25rem', marginTop: '.1rem',
                          }}>
                            <CategoryIcon iconKey={profile.categoryIcon} size={11} />
                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {profile.category}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Stats */}
                      <div style={{ display: 'flex', gap: '.75rem', fontSize: '.75rem', color: 'var(--muted)' }}>
                        {profile.avgRating > 0 && (
                          <span style={{ display: 'flex', alignItems: 'center', gap: '.2rem', color: '#f59e0b' }}>
                            <Star size={11} fill="#f59e0b" /> {profile.avgRating.toFixed(1)}
                          </span>
                        )}
                        {profile.completedCount > 0 && (
                          <span style={{ display: 'flex', alignItems: 'center', gap: '.2rem' }}>
                            <Briefcase size={11} /> {profile.completedCount}
                          </span>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ─── Filtrlər + Sıralama ─── */}
      <div className="fade-up" style={{ marginBottom: '1.5rem', display: 'flex', flexDirection: 'column', gap: '.85rem' }}>

        {/* Axtarış + sıralama + görünüş toggle */}
        <div className="jobs-filter-row" style={{ display: 'flex', gap: '.75rem', flexWrap: 'wrap', alignItems: 'center' }}>
          <input
            className="form-input"
            style={{ maxWidth: 260, flex: '1 1 200px' }}
            placeholder="Axtar..."
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
          />

          {/* Şəhər seçimi */}
          <select
            aria-label="Şəhər seçin"
            className="form-select"
            style={{ maxWidth: 200, flex: '1 1 140px' }}
            value={city}
            onChange={e => handleCity(e.target.value)}
          >
            <option value="">Bütün şəhərlər</option>
            {[...CITIES]
              .sort((a, b) => (cityCounts[b] || 0) - (cityCounts[a] || 0))
              .map(c => (
                <option key={c} value={c}>
                  {c}{cityCounts[c] ? ` (${cityCounts[c]})` : ''}
                </option>
              ))
            }
          </select>

          {/* Bakı rayonu — yalnız Bakı seçiləndə görünür */}
          {isBaku && (
            <select
              aria-label="Rayon seçin"
              className="form-select"
              style={{ maxWidth: 180, flex: '1 1 140px' }}
              value={district}
              onChange={e => handleDistrict(e.target.value)}
            >
              <option value="">Bütün rayonlar</option>
              {BAKU_DISTRICTS.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
          )}

          {/* Sıralama */}
          <select
            aria-label="Sıralama"
            className="form-select"
            style={{ maxWidth: 220, flex: '1 1 170px' }}
            value={sort}
            onChange={e => handleSort(e.target.value)}
          >
            {SORT_OPTIONS.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>

          {/* Grid / List toggle — hidden on mobile via .jobs-view-toggle */}
          <div className="jobs-view-toggle" style={{
            display: 'flex', borderRadius: 'var(--radius-sm)',
            border: '1px solid rgba(255,255,255,.1)', overflow: 'hidden', flexShrink: 0,
          }}>
            {[
              { v: 'grid', icon: (
                <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                  <rect x="1" y="1" width="6" height="6" rx="1"/><rect x="9" y="1" width="6" height="6" rx="1"/>
                  <rect x="1" y="9" width="6" height="6" rx="1"/><rect x="9" y="9" width="6" height="6" rx="1"/>
                </svg>
              )},
              { v: 'list', icon: (
                <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                  <rect x="1" y="2" width="14" height="3" rx="1"/>
                  <rect x="1" y="7" width="14" height="3" rx="1"/>
                  <rect x="1" y="12" width="14" height="3" rx="1"/>
                </svg>
              )},
            ].map(({ v, icon }) => (
              <button key={v} type="button"
                title={v === 'grid' ? 'Grid görünüş' : 'Siyahı görünüş'}
                aria-label={v === 'grid' ? 'Şəbəkə görünüşü' : 'Siyahı görünüşü'}
                onClick={() => { setView(v); saveView(v) }}
                style={{
                  padding: '.45rem .65rem', border: 'none', cursor: 'pointer',
                  background: view === v ? 'rgba(var(--accent-rgb),.15)' : 'rgba(255,255,255,.04)',
                  color: view === v ? 'var(--accent)' : 'var(--muted)',
                  transition: 'all .15s', fontFamily: 'inherit',
                }}>
                {icon}
              </button>
            ))}
          </div>
        </div>

        {/* Kateqoriya axtarışı */}
        <div style={{ position: 'relative', maxWidth: 280 }}>
          <Search
            size={15}
            style={{
              position: 'absolute', left: '.7rem', top: '50%', transform: 'translateY(-50%)',
              color: 'var(--muted)', pointerEvents: 'none',
            }}
          />
          <input
            className="form-input"
            aria-label="Kateqoriya axtar"
            placeholder="Kateqoriya axtar..."
            value={categoryQuery}
            onChange={e => setCategoryQuery(e.target.value)}
            style={{ paddingLeft: '2.1rem', width: '100%' }}
          />
        </div>

        {/* Kateqoriya filtri */}
        <div className="scroll-row-wrap">
          <div className="scroll-row">
            {visibleCategoryOptions.length === 0 && (
              <span style={{ fontSize: '.78rem', color: 'var(--muted)', padding: '.35rem 0' }}>
                Kateqoriya tapılmadı
              </span>
            )}
            {visibleCategoryOptions.map(categoryOption => (
              <button
                key={categoryOption.id || categoryOption.name}
                onClick={() => handleCategory(categoryOption.name)}
                className="btn btn-sm"
                style={{
                  flexShrink: 0,
                  background:  category === categoryOption.name ? 'rgba(var(--accent-rgb),.15)'  : 'rgba(255,255,255,.04)',
                  borderColor: category === categoryOption.name ? 'rgba(var(--accent-rgb),.5)'   : 'rgba(255,255,255,.1)',
                  color:       category === categoryOption.name ? 'var(--accent)'         : 'var(--muted)',
                  fontWeight:  category === categoryOption.name ? 600                     : 400,
                  whiteSpace: 'nowrap',
                }}
              >
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '.35rem' }}>
                  {categoryOption.name !== 'Hamısı' && <CategoryIcon iconKey={categoryOption.iconKey} size={14} />}
                  {categoryOption.name}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Aktiv filtr göstəricisi */}
        {(city || district || category !== 'Hamısı') && (
          <div style={{ display: 'flex', gap: '.4rem', flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ fontSize: '.72rem', color: 'var(--muted)' }}>Aktiv filtr:</span>
            {city && (
              <span style={filterChipStyle}>
                <MapPin size={11} /> {city}{district ? `, ${district}` : ''}
                <button onClick={() => handleCity('')} aria-label="Şəhər filtrini sil" style={chipCloseStyle}>✕</button>
              </span>
            )}
            {category !== 'Hamısı' && (
              <span style={filterChipStyle}>
                {category}
                <button onClick={() => handleCategory('Hamısı')} aria-label="Kateqoriya filtrini sil" style={chipCloseStyle}>✕</button>
              </span>
            )}
            <button
              className="btn btn-sm"
              style={{ fontSize: '.72rem', padding: '.2rem .55rem', color: 'var(--danger)', borderColor: 'rgba(248,113,113,.3)' }}
              onClick={() => { handleCity(''); handleCategory('Hamısı'); setSearchInput(''); setCategoryQuery(''); }}
            >
              Hamısını sıfırla
            </button>
          </div>
        )}
      </div>

      {/* Grid */}
      {loading ? (
        <div className="page-loading"><div className="spinner" /></div>
      ) : jobs.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon" style={{ display: 'flex', justifyContent: 'center' }}><ClipboardList size={36} color="var(--muted)" /></div>
          <div className="empty-state-title">Elan tapılmadı</div>
          <div className="empty-state-desc">Axtarış parametrlərini dəyişin</div>
        </div>
      ) : (
        <div
          key={view}
          className="jobs-feed"
          style={
            view === 'list'
              ? { display: 'flex', flexDirection: 'column', gap: '1rem' }
              : { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1.5rem' }
          }
        >
          {gridItems.map((item, idx) =>
            item.type === 'ad'
              ? <AdCard key={`ad-${item.data.id}-${idx}`} ad={item.data} />
              : <JobCard key={item.data.id} job={item.data} view={view} index={idx} />
          )}
        </div>
      )}

      {/* Pagination */}
      {!loading && totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center',
          gap: '.5rem', marginTop: '2.5rem', flexWrap: 'wrap' }}>
          <button className="btn btn-sm" onClick={() => setPage(p => p - 1)}
            disabled={page === 0} style={pagerStyle(false, page === 0)}>
            ← Əvvəlki
          </button>

          {pages[0] > 0 && (
            <>
              <button className="btn btn-sm" onClick={() => setPage(0)} style={pagerStyle(page === 0, false)}>1</button>
              {pages[0] > 1 && <span style={{ color: 'var(--muted)', padding: '0 .25rem' }}>…</span>}
            </>
          )}

          {pages.map(p => (
            <button key={p} className="btn btn-sm" onClick={() => setPage(p)} style={pagerStyle(p === page, false)}>
              {p + 1}
            </button>
          ))}

          {pages[pages.length - 1] < totalPages - 1 && (
            <>
              {pages[pages.length - 1] < totalPages - 2 && (
                <span style={{ color: 'var(--muted)', padding: '0 .25rem' }}>…</span>
              )}
              <button className="btn btn-sm" onClick={() => setPage(totalPages - 1)}
                style={pagerStyle(page === totalPages - 1, false)}>
                {totalPages}
              </button>
            </>
          )}

          <button className="btn btn-sm" onClick={() => setPage(p => p + 1)}
            disabled={page >= totalPages - 1} style={pagerStyle(false, page >= totalPages - 1)}>
            Növbəti →
          </button>
        </div>
      )}

      {!loading && totalPages > 1 && (
        <p style={{ textAlign: 'center', color: 'var(--muted)', fontSize: '.8rem', marginTop: '.75rem' }}>
          Səhifə {page + 1} / {totalPages} · Cəmi {totalElements} elan
        </p>
      )}
    </div>
  )
}

function pagerStyle(active, disabled) {
  return {
    minWidth: '2.4rem',
    background:  active   ? 'rgba(var(--accent-rgb),.15)' : 'rgba(255,255,255,.04)',
    borderColor: active   ? 'rgba(var(--accent-rgb),.5)'  : 'rgba(255,255,255,.1)',
    color:       active   ? 'var(--accent)'        : disabled ? 'rgba(255,255,255,.25)' : 'var(--muted)',
    cursor:      disabled ? 'not-allowed'          : 'pointer',
    fontWeight:  active   ? 700                    : 400,
  }
}

const filterChipStyle = {
  display: 'inline-flex', alignItems: 'center', gap: '.3rem',
  fontSize: '.8rem', fontWeight: 600,
  padding: '.25rem .65rem', borderRadius: '100px',
  background: 'rgba(var(--accent-rgb),.08)',
  border: '1px solid rgba(var(--accent-rgb),.2)',
  color: 'var(--accent)',
}

const chipCloseStyle = {
  background: 'none', border: 'none', cursor: 'pointer',
  color: 'var(--muted)', fontSize: '.7rem', padding: '0', lineHeight: 1,
  fontFamily: 'inherit',
}
